import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES, SERVICE_AREAS } from "../data/site";
import { LANDING_PRODUCTS, LANDING_PAGES } from "../data/landing";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";

const TOP_LEVEL = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "All Services" },
    { to: "/service-areas", label: "All Service Areas" },
    { to: "/reviews", label: "Reviews" },
    { to: "/contractors", label: "For Contractors" },
    { to: "/contact", label: "Contact / Free Quote" },
];

export default function Sitemap() {
    useReveal();
    useSeo({
        title: "Sitemap | HAZ Insulation",
        description:
            "Browse every page on hazinsulation.com — services, service areas, and SEO landing pages for spray foam, blown-in, attic, crawl space and more.",
    });

    return (
        <main data-testid="sitemap-page" className="pt-24 pb-24 bg-bone">
            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        <span className="h-px w-10 bg-moss inline-block align-middle mr-3" />
                        Sitemap
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mb-14 reveal">
                        Every page on hazinsulation.com.
                    </h1>

                    {/* Top-level */}
                    <div className="mb-16">
                        <h2 className="font-heading text-2xl font-bold mb-6 reveal">
                            Main Pages
                        </h2>
                        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                            {TOP_LEVEL.map((t) => (
                                <li key={t.to}>
                                    <Link
                                        to={t.to}
                                        className="link-underline text-base font-medium text-ink hover:text-copper"
                                    >
                                        {t.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
                        <div>
                            <h2 className="font-heading text-2xl font-bold mb-6 reveal">
                                Services
                            </h2>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                {SERVICES.map((s) => (
                                    <li key={s.id}>
                                        <Link
                                            to={`/services/${s.id}`}
                                            data-testid={`sitemap-service-${s.id}`}
                                            className="link-underline text-base font-medium text-ink hover:text-copper"
                                        >
                                            {s.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mb-6 reveal">
                                Service Areas
                            </h2>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                {SERVICE_AREAS.map((a) => (
                                    <li key={a.slug}>
                                        <Link
                                            to={`/areas/${a.slug}`}
                                            data-testid={`sitemap-area-${a.slug}`}
                                            className="link-underline text-base font-medium text-ink hover:text-copper"
                                        >
                                            {a.city}, {a.state}{" "}
                                            <span className="text-xs text-muted_ink">
                                                {a.zip}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* SEO landing pages, grouped by product */}
                    <div className="mb-16">
                        <h2 className="font-heading text-2xl font-bold mb-2 reveal">
                            City + Service Pages
                        </h2>
                        <p className="text-muted_ink mb-8 reveal">
                            {LANDING_PAGES.length} dedicated pages — one for each product +
                            city we serve.
                        </p>
                        <div className="space-y-12">
                            {LANDING_PRODUCTS.map((p) => (
                                <div key={p.keyword}>
                                    <h3 className="font-heading text-lg font-bold mb-4 text-copper">
                                        {p.label}
                                    </h3>
                                    <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
                                        {LANDING_PAGES
                                            .filter((lp) => lp.product.keyword === p.keyword)
                                            .map((lp) => (
                                                <li key={lp.slug}>
                                                    <Link
                                                        to={`/${lp.slug}`}
                                                        className="link-underline text-sm text-ink hover:text-copper"
                                                    >
                                                        {lp.area.city}, {lp.area.state}
                                                    </Link>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="reveal">
                        <Link
                            to="/"
                            className="btn-lift inline-flex items-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                            data-testid="sitemap-back-home"
                        >
                            Back to homepage <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
